// store/user.ts
import { atom } from 'nanostores';

// Types
interface User {
  id: string;
  roles: string[];
  apps: string[];
  tenantId: number;
  picture?: string;
  name?: string;
  email?: string;
  userName?: string;
  tenantName?: string;
  domain?: string;
}

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  loading: boolean;
}

// Auth state store
export const $auth = atom<AuthState>({
  isAuthenticated: false,
  user: null,
  accessToken: null,
  refreshToken: null,
  loading: false
});

// Helper function to check if token is expired
function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const now = Math.floor(Date.now() / 1000);
    return payload.exp <= now;
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true; // Consider invalid token as expired
  }
}

// Function to fetch user info from the API
async function fetchUserInfo(accessToken: string): Promise<User | null> {
  try {
    // Check if token is expired before making API call
    if (isTokenExpired(accessToken)) {
      console.warn('Access token is expired');
      logout();
      return null;
    }

    const response = await fetch('https://pms-auth.timescolink.com/api/v1/auth/validate-token', {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'X-Device-Type': 'desktop',
        'X-App-Name': 'HRMS',
        'Authorization': `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        console.error('Token validation failed, clearing session');
        logout();
      }
      console.error('Failed to fetch user info:', response.status);
      return null;
    }

    const data = await response.json();
    
    // Decode JWT to get additional info like user ID
    const payload = JSON.parse(atob(accessToken.split('.')[1]));
    
    return {
      id: payload.sub,
      roles: Array.isArray(data.role) ? data.role : [data.role],
      apps: data.apps || [],
      tenantId: payload.tenant_id,
      name: data.user_name,
      email: data.email,
      userName: data.user_name,
      tenantName: data.tenant_name,
      domain: data.domain,
      picture: payload.picture, // Keep from JWT if available
    };
  } catch (error) {
    console.error('Error fetching user info:', error);
    return null;
  }
}

// Function to set tokens and update auth state
export async function setTokens(accessToken: string, refreshToken: string): Promise<void> {
  try {
    // Check if tokens are expired before setting them
    if (isTokenExpired(accessToken)) {
      console.warn('Cannot set expired access token');
      logout();
      return;
    }

    // Set loading state
    $auth.set({ ...$auth.get(), loading: true });

    // Fetch user info from API
    const user = await fetchUserInfo(accessToken);
    
    if (user) {
      // Update the auth store with user info from API
      $auth.set({
        isAuthenticated: true,
        user,
        accessToken,
        refreshToken,
        loading: false
      });

      // Store tokens in localStorage for persistence
      localStorage.setItem('access_token', accessToken);
      localStorage.setItem('refresh_token', refreshToken);
      
    } else {
      // If API call fails, fall back to JWT info only
      console.warn('Failed to fetch user info from API, using JWT data only');
      
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      const fallbackUser: User = {
        id: payload.sub,
        roles: payload.roles || [],
        apps: payload.apps || [],
        tenantId: payload.tenant_id,
        name: payload.name,
        email: payload.email,
        picture: payload.picture
      };

      $auth.set({
        isAuthenticated: true,
        user: fallbackUser,
        accessToken,
        refreshToken,
        loading: false
      });

      localStorage.setItem('access_token', accessToken);
      localStorage.setItem('refresh_token', refreshToken);
    }
  } catch (error) {
    console.error('Error setting tokens:', error);
    // If token parsing fails, clear everything
    logout();
  }
}

export function extractTokensFromURL() {
  const url = new URL(window.location.href);
  const accessToken = url.searchParams.get('access_token');
  const refreshToken = url.searchParams.get('refresh_token');

  if (accessToken && refreshToken) {
    // Check if token is expired before setting
    if (isTokenExpired(accessToken)) {
      console.warn('Received expired token from URL, clearing session');
      logout();
      // Clean up URL anyway
      url.searchParams.delete('access_token');
      url.searchParams.delete('refresh_token');
      window.history.replaceState({}, document.title, url.pathname + url.search);
      return;
    }

    // Only set tokens if user is not already authenticated
    const current = $auth.get();
    if (!current.isAuthenticated || current.accessToken !== accessToken) {
      setTokens(accessToken, refreshToken);
    }

    // Clean up URL
    url.searchParams.delete('access_token');
    url.searchParams.delete('refresh_token');
    window.history.replaceState({}, document.title, url.pathname + url.search);
  }
}

// Function to check if user is authenticated
export async function checkSession(): Promise<void> {
  $auth.set({ ...$auth.get(), loading: true });
  
  try {
    // First check if tokens are already in the store
    const currentAuth = $auth.get();
    if (currentAuth.accessToken && currentAuth.isAuthenticated && currentAuth.user?.email) {
      // Check if current token is expired
      if (isTokenExpired(currentAuth.accessToken)) {
        console.warn('Current session token expired, clearing session');
        logout();
        return;
      }
      $auth.set({ ...currentAuth, loading: false });
      return; // Already authenticated with complete user info
    }
    
    // Check localStorage for existing tokens
    const storedAccessToken = localStorage.getItem('access_token');
    const storedRefreshToken = localStorage.getItem('refresh_token');
    
    if (storedAccessToken && storedRefreshToken) {
      // Check if stored token is expired
      if (isTokenExpired(storedAccessToken)) {
        console.warn('Stored access token expired, attempting refresh');
        // Token expired, try to refresh or logout
        await refreshAccessToken();
      } else {
        // Token is still valid, fetch user info
        await setTokens(storedAccessToken, storedRefreshToken);
      }
    } else {
      // No tokens found, user not authenticated
      $auth.set({
        isAuthenticated: false,
        user: null,
        accessToken: null,
        refreshToken: null,
        loading: false
      });
    }
  } catch (error) {
    console.error('Error checking session:', error);
    logout();
  }
}

// Function to refresh access token
export async function refreshAccessToken(): Promise<void> {
  try {
    const refreshToken = $auth.get().refreshToken || localStorage.getItem('refresh_token');
    
    if (!refreshToken) {
      console.warn('No refresh token available, logging out');
      logout();
      return;
    }

    // Check if refresh token is expired (if it's a JWT)
    try {
      if (isTokenExpired(refreshToken)) {
        console.warn('Refresh token is expired, logging out');
        logout();
        return;
      }
    } catch {
      // Refresh token might not be a JWT, continue with refresh attempt
    }

    // Make API call to refresh token
    const response = await fetch(`${import.meta.env.PUBLIC_SSO_BASE_URL || 'https://sso.werkiq.com'}/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (response.ok) {
      const data = await response.json();
      await setTokens(data.access_token, data.refresh_token || refreshToken);
    } else {
      // Refresh failed, logout user
      console.warn('Token refresh failed:', response.status);
      logout();
    }
  } catch (error) {
    console.error('Error refreshing token:', error);
    logout();
  }
}

// Function to logout user
export function logout(): void {
  // Clear tokens from localStorage
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  
  // Reset auth state
  $auth.set({
    isAuthenticated: false,
    user: null,
    accessToken: null,
    refreshToken: null,
    loading: false
  });
  
 
  
  // Optionally redirect to home page or show login
  // window.location.href = '/';
}

// Function to get authorization header for API calls
export function getAuthHeader(): string | null {
  const { accessToken } = $auth.get();
  
  if (!accessToken) {
    return null;
  }
  
  // Check if token is expired
  if (isTokenExpired(accessToken)) {
    console.warn('Access token expired, clearing session');
    logout();
    return null;
  }
  
  return `Bearer ${accessToken}`;
}

// Function to make authenticated API calls
export async function authenticatedFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const authHeader = getAuthHeader();
  
  if (!authHeader) {
    throw new Error('User not authenticated');
  }
  
  const response = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': authHeader,
      'Accept': 'application/json',
      'X-Device-Type': 'desktop',
      'X-App-Name': 'HRMS',
    },
  });
  
  // If token expired or unauthorized, try to refresh
  if (response.status === 401 || response.status === 403) {
    console.warn('API call failed with auth error, attempting token refresh');
    await refreshAccessToken();
    
    // Retry with new token if refresh was successful
    const newAuthHeader = getAuthHeader();
    if (newAuthHeader) {
      return fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          'Authorization': newAuthHeader,
          'Accept': 'application/json',
          'X-Device-Type': 'desktop',
          'X-App-Name': 'HRMS',
        },
      });
    } else {
      throw new Error('Authentication failed after token refresh');
    }
  }
  
  return response;
}

// Helper function to check if user has specific role
export function hasRole(role: string): boolean {
  const { user } = $auth.get();
  return user?.roles.includes(role) || false;
}

// Helper function to check if user has access to specific app
export function hasAppAccess(app: string): boolean {
  const { user } = $auth.get();
  return user?.apps.includes(app) || false;
}

// Helper function to get current user
export function getCurrentUser(): User | null {
  return $auth.get().user;
}

// Helper function to check if user is authenticated
export function isAuthenticated(): boolean {
  const auth = $auth.get();
  if (!auth.isAuthenticated || !auth.accessToken) {
    return false;
  }
  
  // Check if token is expired
  if (isTokenExpired(auth.accessToken)) {
    logout();
    return false;
  }
  
  return true;
}

// Helper function to get user email
export function getUserEmail(): string | null {
  return $auth.get().user?.email || null;
}

// Helper function to get user name
export function getUserName(): string | null {
  return $auth.get().user?.userName || $auth.get().user?.name || null;
}

// Helper function to get tenant info
export function getTenantInfo(): { name?: string; domain?: string } | null {
  const user = $auth.get().user;
  if (!user) return null;
  
  return {
    name: user.tenantName,
    domain: user.domain
  };
}

export default $auth;